import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import ar from 'date-fns/locale/ar-EG';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { fetchBookings } from '../../../../api';
import {
  BOOKING_STATUS_LABELS,
  BOOKING_TYPE_LABELS,
  formatNumber,
} from '../../../../shared/utils/formatters';

import styles from './AdvancedCalendar.module.css';

const locales = { ar };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 6 }),
  getDay,
  locales,
});

const STATUS_CLASS = {
  pending: styles.pending,
  confirmed: styles.confirmed,
  in_progress: styles.inProgress,
  completed: styles.completed,
  cancelled: styles.cancelled,
};

const NEXT_ACTION = {
  pending: { text: 'تأكيد الحجز', nextStatus: 'confirmed', className: styles.confirmButton },
  confirmed: { text: 'بدء التنفيذ', nextStatus: 'in_progress', className: styles.startButton },
  in_progress: { text: 'إكمال الحجز', nextStatus: 'completed', className: styles.completeButton },
};

const toDateOnly = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const EventComponent = ({ event }) => {
  const booking = event.resource;
  return (
    <div className={`${styles.eventCard} ${STATUS_CLASS[booking.status] || styles.pending}`}>
      <span className={styles.eventTitle}>{booking.client?.name || 'عميل غير محدد'}</span>
      <span className={styles.eventMeta}>
        {BOOKING_TYPE_LABELS[booking.booking_type] || booking.booking_type}
      </span>
    </div>
  );
};

const BookingQuickView = ({ booking, onClose, onStatusUpdate }) => {
  const navigate = useNavigate();
  const nextAction = NEXT_ACTION[booking.status];
  const remaining = parseFloat(booking.remaining_amount || 0);

  const handleStatusClick = async () => {
    if (!nextAction) return;
    await onStatusUpdate(booking.id, nextAction.nextStatus);
    onClose();
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h3>{booking.client?.name || 'عميل غير محدد'}</h3>
            <p>{BOOKING_TYPE_LABELS[booking.booking_type] || booking.booking_type}</p>
          </div>
          <button className={styles.closeButton} type="button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.detailGrid}>
            <div>
              <span>تاريخ الحجز</span>
              <strong>{booking.booking_date}</strong>
            </div>
            <div>
              <span>الحالة</span>
              <strong>{BOOKING_STATUS_LABELS[booking.status] || booking.status}</strong>
            </div>
          </div>

          <div className={styles.financeGrid}>
            <div>
              <span>الإجمالي</span>
              <strong>{formatNumber(booking.total_price)}</strong>
            </div>
            <div>
              <span>المدفوع</span>
              <strong className={styles.paid}>{formatNumber(booking.total_paid)}</strong>
            </div>
            <div>
              <span>{remaining < 0 ? 'دفع زيادة' : remaining === 0 ? 'مكتمل' : 'المتبقي'}</span>
              <strong className={remaining > 0 ? styles.remaining : styles.paid}>
                {remaining < 0
                  ? `+ ${formatNumber(Math.abs(remaining))}`
                  : formatNumber(booking.remaining_amount)}
              </strong>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          {nextAction && (
            <button
              type="button"
              className={`${styles.actionButton} ${nextAction.className}`}
              onClick={handleStatusClick}
            >
              {nextAction.text}
            </button>
          )}
          <button
            type="button"
            className={styles.detailsButton}
            onClick={() => {
              navigate(`/booking/${booking.id}`);
              onClose();
            }}
          >
            عرض التفاصيل
          </button>
        </div>
      </div>
    </div>
  );
};

const AdvancedCalendar = ({ filters, onStatusUpdate }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    let ignore = false;

    const loadCalendarBookings = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchBookings({
          ...filters,
          page: 1,
          page_size: 1000,
        });
        const data = response.data;
        const list = data?.results !== undefined ? data.results : data;
        if (!ignore) setBookings(Array.isArray(list) ? list : []);
      } catch {
        if (!ignore) setError('فشل في تحميل تقويم الحجوزات.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadCalendarBookings();

    return () => {
      ignore = true;
    };
  }, [filters]);

  const events = useMemo(() => (
    bookings
      .map((booking) => {
        const bookingDate = toDateOnly(booking.booking_date);
        if (!bookingDate) return null;
        return {
          title: booking.client?.name || 'حجز بدون اسم عميل',
          start: bookingDate,
          end: bookingDate,
          allDay: true,
          resource: booking,
        };
      })
      .filter(Boolean)
  ), [bookings]);

  const dayPropGetter = useCallback((date) => {
    const today = new Date();
    const isToday =
      date.getFullYear() === today.getFullYear()
      && date.getMonth() === today.getMonth()
      && date.getDate() === today.getDate();

    return isToday ? { className: styles.todayCell } : {};
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.calendarHeader}>
        <div>
          <strong>{bookings.length}</strong>
          <span>حجز في التقويم حسب الفلاتر الحالية</span>
        </div>
        {loading && <span className={styles.loading}>جاري التحديث...</span>}
        {error && <span className={styles.error}>{error}</span>}
      </div>

      <div className={styles.calendarContainer}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 720 }}
          culture="ar"
          rtl
          components={{ event: EventComponent }}
          onSelectEvent={(event) => setSelectedBooking(event.resource)}
          dayPropGetter={dayPropGetter}
          views={['month']}
          messages={{
            month: 'الشهر',
            week: 'الأسبوع',
            day: 'اليوم',
            agenda: 'أجندة',
            today: 'اليوم',
            previous: 'السابق',
            next: 'التالي',
            showMore: (total) => `+${total} المزيد`,
          }}
        />
      </div>

      {selectedBooking && (
        <BookingQuickView
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onStatusUpdate={onStatusUpdate}
        />
      )}
    </div>
  );
};

export default AdvancedCalendar;
